import { Cache } from "swr";

/**
 * SWR cache extension that bounds how many parsed workout details stay in
 * memory.
 *
 * Every activity opened in Activity Details caches its full parsed FIT
 * payload (including the per-second records array, ~4-7 MB per multi-hour
 * activity) under an SWR key of the shape ["workout-details", <entity>,
 * <gapEnabled>]. SWR's cache is never evicted, so browsing activities
 * accumulated tens-to-hundreds of MB over a session and contributed to
 * webview crashes (see DEX-882).
 *
 * This provider delegates every operation to the parent (global) cache — so
 * everything else (workout summaries, feature flags, the shared crux-js WASM
 * instance) keeps its normal SWR behavior — and additionally tracks
 * workout-details keys in LRU order, deleting the oldest entries beyond
 * MAX_CACHED_WORKOUT_DETAILS. The LRU list is module-level so it keeps
 * governing entries written to the global cache even while the details route
 * is unmounted.
 */

// Headroom, not a tight bound: the LRU has no notion of "currently
// subscribed", so a still-mounted consumer's entry (e.g. Activity Details
// open while the share drawer shows a different activity) can be evicted if
// enough other entities get cached first — and pull-to-refresh's
// mutate(() => true) sweep get()s every key, flattening recency to insertion
// order. Realistic simultaneous need is 3-4 entries; the rest is slack for
// those two effects.
export const MAX_CACHED_WORKOUT_DETAILS = 6;

// Array SWR keys serialize as `@"workout-details",<...>,` (swr 2.x
// unstable_serialize), so the cache sees this string prefix.
const WORKOUT_DETAILS_KEY_PREFIX = '@"workout-details",';

// Most-recently-used keys last.
const recentWorkoutDetailsKeys: string[] = [];

const isWorkoutDetailsKey = (key: string) => key.startsWith(WORKOUT_DETAILS_KEY_PREFIX);

const markRecentlyUsed = (key: string) => {
  const existingIndex = recentWorkoutDetailsKeys.indexOf(key);
  if (existingIndex !== -1) {
    recentWorkoutDetailsKeys.splice(existingIndex, 1);
  }
  recentWorkoutDetailsKeys.push(key);
};

const forgetKey = (key: string) => {
  const existingIndex = recentWorkoutDetailsKeys.indexOf(key);
  if (existingIndex !== -1) {
    recentWorkoutDetailsKeys.splice(existingIndex, 1);
  }
};

const evictLeastRecentlyUsed = (cache: Cache) => {
  while (recentWorkoutDetailsKeys.length > MAX_CACHED_WORKOUT_DETAILS) {
    const oldestKey = recentWorkoutDetailsKeys.shift();
    if (oldestKey !== undefined) {
      cache.delete(oldestKey);
    }
  }
};

/**
 * SWRConfig `provider` — wraps the parent cache with LRU eviction for
 * workout-details entries. Pass as `provider: extendCacheWithWorkoutDetailsLru`.
 */
export function extendCacheWithWorkoutDetailsLru(parentCache: Cache): Cache {
  return {
    keys: () => parentCache.keys(),
    get: (key: string) => {
      if (isWorkoutDetailsKey(key) && recentWorkoutDetailsKeys.indexOf(key) !== -1) {
        markRecentlyUsed(key);
      }
      return parentCache.get(key);
    },
    set: (key: string, value: unknown) => {
      parentCache.set(key, value as never);
      if (isWorkoutDetailsKey(key)) {
        markRecentlyUsed(key);
        evictLeastRecentlyUsed(parentCache);
      }
    },
    delete: (key: string) => {
      if (isWorkoutDetailsKey(key)) {
        forgetKey(key);
      }
      parentCache.delete(key);
    },
  };
}

/** Test-only: reset the module-level LRU bookkeeping. */
export function __resetWorkoutDetailsLruForTests() {
  recentWorkoutDetailsKeys.length = 0;
}
